/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, ReactNode, createContext, useContext, useMemo } from 'react';

interface SubdomainContextType {
  isDashboardSubdomain: boolean;
  subdomain: string;
}

const SubdomainContext = createContext<SubdomainContextType | undefined>(undefined);

/**
 * Hook to use the subdomain context.
 * @returns The subdomain context value.
 */
export const useSubdomain = (): SubdomainContextType => {
  const context = useContext(SubdomainContext);
  if (!context) {
    throw new Error('useSubdomain must be used within SubdomainProvider');
  }
  return context;
};

interface SubdomainProviderProps {
  children: ReactNode;
}

const DASHBOARD_SUBDOMAINS = [
  'dashboard',
  'dashboardv2',
  'dashboard-v2',
  'devdashboardv2',
  'dev-dashboardv2',
  'dev-dashboard-v2'
];

export function SubdomainProvider({ children }: SubdomainProviderProps): ReactElement {
  const subdomainInfo = useMemo(() => {
    const { hostname } = window.location;

    // Extract subdomain from hostname
    // For local development, handle localhost separately
    // A tunnelled dev host (ngrok and friends) gets a random subdomain that can
    // never match the list below, which drops the whole app into public-link-only
    // mode. REACT_APP_FORCE_DASHBOARD_HOST lets a local .env opt out of the check
    // so the app can be opened from a phone; it is unset in every deployed build.
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      process.env.REACT_APP_FORCE_DASHBOARD_HOST === 'true'
    ) {
      // During development, treat localhost as dashboard subdomain
      return {
        subdomain: 'dashboard',
        isDashboardSubdomain: true
      };
    }

    // Split hostname to get subdomain
    const parts = hostname.split('.');

    // If hostname is "dashboard.telosair.com", subdomain is "dashboard"
    // If hostname is "anything-else.telosair.com", subdomain is "anything-else"
    // If hostname is "telosair.com", no subdomain
    const subdomain = parts.length > 2 ? parts[0] : '';
    const normalizedSubdomain = subdomain.toLowerCase();
    const isDashboardSubdomain = DASHBOARD_SUBDOMAINS.includes(normalizedSubdomain);
    console.log({ subdomain, normalizedSubdomain, isDashboardSubdomain });
    return {
      subdomain,
      isDashboardSubdomain
    };
  }, []);

  return <SubdomainContext.Provider value={subdomainInfo}>{children}</SubdomainContext.Provider>;
}
